import { configureRequestParams, getErrorMessage } from '@/utils/helpers'

import axios from './instance'

/**
 * Gets the list of EAPs for an entity
 *
 * @param {object} payload - The request parameters
 * @param {string} payload.entityId - The entity ID
 * @param {Array} payload.sorting - Sorting configuration
 * @param {object} payload.pagination - Pagination configuration
 * @param {string} payload.searchTerm - Search term
 * @returns {Promise<object>} The EAPs for the specified entity
 */
export const getEAPs = async (payload) => {
  const { entityId, sorting, pagination, searchTerm } = payload
  const params = configureRequestParams(sorting, pagination, searchTerm)
  try {
    return await axios.get(`/entities/${entityId}/eaps/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Get a single EAP by ID
 *
 * @param {object} payload - The request parameters
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.eapId - The EAP ID
 * @returns {Promise<object>} The EAP
 */
export const getEAP = async ({ entityId, eapId }) => {
  try {
    return await axios.get(`/entities/${entityId}/eaps/${eapId}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Add a new EAP
 *
 * @param {object} payload - The EAP data to be added
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.name - The EAP name
 * @param {string} payload.description - The EAP description (optional)
 * @param {Array} payload.jobIds - Job IDs that have access to the EAP
 * @param {Array} payload.questions - Questions array
 * @param {string} payload.extricationVideoPath - Path to extrication video (optional)
 * @param {Array} payload.extricationVideoJobIds - Job IDs with access to video (optional)
 * @returns {Promise<object>} The added EAP
 */
export const addEAP = async ({ entityId, ...data }) => {
  try {
    return await axios.post(`/entities/${entityId}/eaps/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update an EAP
 *
 * @param {object} payload - The EAP data to be updated
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.id - The EAP ID to be updated
 * @returns {Promise<object>} The updated EAP
 */
export const updateEAP = async ({ entityId, id, ...data }) => {
  try {
    return await axios.patch(`/entities/${entityId}/eaps/${id}/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Deletes an EAP
 *
 * @param {object} payload - The EAP data to be deleted
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.id - The EAP ID to be deleted
 * @returns {Promise<object>} 204 No Content if successful
 */
export const deleteEAP = async ({ entityId, id }) => {
  try {
    return await axios.delete(`/entities/${entityId}/eaps/${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Get quiz questions for an EAP (student view - answers hidden)
 *
 * @param {object} payload - The request parameters
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.eapId - The EAP ID
 * @returns {Promise<Array>} The quiz questions
 */
export const getEAPQuiz = async ({ entityId, eapId }) => {
  try {
    return await axios.get(`/entities/${entityId}/eaps/${eapId}/quiz/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Submit quiz answers and get scored result
 *
 * @param {object} payload - The quiz submission data
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.eapId - The EAP ID
 * @param {Array} payload.responses - Array of responses [{questionId, selectedAnswer}]
 * @returns {Promise<object>} The quiz attempt result
 */
export const submitEAPQuiz = async ({ entityId, eapId, responses }) => {
  try {
    return await axios.post(`/entities/${entityId}/eaps/${eapId}/take_quiz/`, { responses })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Get user's quiz status for an EAP
 *
 * @param {object} payload - The request parameters
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.eapId - The EAP ID
 * @returns {Promise<object>} The quiz status {hasQuiz, lastAttempt, nextDueDate, isDue}
 */
export const getEAPQuizStatus = async ({ entityId, eapId }) => {
  try {
    return await axios.get(`/entities/${entityId}/eaps/${eapId}/quiz_status/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Get quiz compliance status for all users with access to an EAP
 *
 * @param {object} payload - The request parameters
 * @param {string} payload.entityId - The entity ID
 * @param {string} payload.eapId - The EAP ID
 * @returns {Promise<object>} The compliance data with user statuses
 */
export const getEAPCompliance = async ({ entityId, eapId }) => {
  try {
    return await axios.get(`/entities/${entityId}/eaps/${eapId}/compliance/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
